"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CLIENT_CONFIG = void 0;
const utils_1 = require("./utils");
const document_1 = __importDefault(require("./document"));
const dids_1 = require("dids");
const common_1 = require("@ceramicnetwork/common");
const doctype_tile_1 = require("@ceramicnetwork/doctype-tile");
const doctype_caip10_link_1 = require("@ceramicnetwork/doctype-caip10-link");
const _3id_did_resolver_1 = __importDefault(require("@ceramicnetwork/3id-did-resolver"));
const key_did_resolver_1 = __importDefault(require("key-did-resolver"));
const did_resolver_1 = require("did-resolver");
const API_PATH = '/api/v0';
const CERAMIC_HOST = 'http://localhost:7007';
exports.DEFAULT_CLIENT_CONFIG = {
    docSyncEnabled: false,
    docSyncInterval: 5000,
    docCacheLimit: 500,
    cacheDocCommits: false,
};
class CeramicClient {
    constructor(apiHost = CERAMIC_HOST, config = {}) {
        this._config = { ...exports.DEFAULT_CLIENT_CONFIG, ...config };
        this._apiUrl = utils_1.combineURLs(apiHost, API_PATH);
        this._docCache = new common_1.DocCache(config.docCacheLimit, this._config.cacheDocCommits);
        this.context = { api: this };
        this.pin = this._initPinApi();
        const keyDidResolver = key_did_resolver_1.default.getResolver();
        const threeIdResolver = _3id_did_resolver_1.default.getResolver(this);
        this.context.resolver = new did_resolver_1.Resolver({
            ...this._config.didResolver, ...threeIdResolver, ...keyDidResolver,
        });
        this._doctypeConstructors = {
            'tile': doctype_tile_1.TileDoctype,
            'caip10-link': doctype_caip10_link_1.Caip10LinkDoctype
        };
    }
    get did() {
        return this.context.did;
    }
    _initPinApi() {
        return {
            add: async (docId) => {
                await utils_1.fetchJson(this._apiUrl + '/pins' + `/${docId.toString()}`, { method: 'post' });
            },
            rm: async (docId) => {
                await utils_1.fetchJson(this._apiUrl + '/pins' + `/${docId.toString()}`, { method: 'delete' });
            },
            ls: async (docId) => {
                let url = this._apiUrl + '/pins';
                if (docId) {
                    url += `/${docId.toString()}`;
                }
                const result = await utils_1.fetchJson(url);
                const { pinnedDocIds } = result;
                return {
                    [Symbol.asyncIterator]() {
                        let index = 0;
                        return {
                            next() {
                                if (index === pinnedDocIds.length) {
                                    return Promise.resolve({ value: null, done: true });
                                }
                                return Promise.resolve({ value: pinnedDocIds[index++], done: false });
                            }
                        };
                    }
                };
            }
        };
    }
    async createDocument(doctype, params, opts) {
        const doctypeConstructor = this.findDoctypeConstructor(doctype);
        const genesis = await doctypeConstructor.makeGenesis(params, this.context, opts);
        return await this.createDocumentFromGenesis(doctype, genesis, opts);
    }
    async createDocumentFromGenesis(doctype, genesis, opts) {
        const doc = await document_1.default.createFromGenesis(this._apiUrl, doctype, genesis, this.context, opts, this._config);
        let docFromCache = this._docCache.get(doc.id);
        if (docFromCache == null) {
            this._docCache.put(doc);
            docFromCache = doc;
        }
        else if (!common_1.DoctypeUtils.statesEqual(doc.state, docFromCache.state)) {
            docFromCache.state = doc.state;
            docFromCache.emit('change');
        }
        doc.doctypeLogic = this.findDoctypeConstructor(doc.state.doctype);
        return docFromCache;
    }
    async loadDocument(docId) {
        docId = utils_1.typeDocID(docId);
        let doc = this._docCache.get(docId);
        if (doc == null) {
            doc = await document_1.default.load(docId, this._apiUrl, this.context, this._config);
            this._docCache.put(doc);
        }
        else {
            await doc._syncState();
        }
        doc.doctypeLogic = this.findDoctypeConstructor(doc.state.doctype);
        return doc;
    }
    async multiQuery(queries) {
        const queriesJSON = queries.map(q => {
            return {
                docId: typeof q.docId === 'string' ? q.docId : q.docId.toString(),
                paths: q.paths
            };
        });
        const results = await utils_1.fetchJson(this._apiUrl + '/multiqueries', {
            method: 'post',
            body: {
                queries: queriesJSON
            }
        });
        return Object.entries(results).reduce((acc, e) => {
            const [k, v] = e;
            const state = common_1.DoctypeUtils.deserializeState(v);
            acc[k] = new document_1.default(state, this.context, this._apiUrl, this._config);
            return acc;
        }, {});
    }
    loadDocumentCommits(docId) {
        const effectiveDocId = utils_1.typeDocID(docId);
        return document_1.default.loadDocumentCommits(effectiveDocId, this._apiUrl);
    }
    async loadDocumentRecords(docId) {
        return this.loadDocumentCommits(docId);
    }
    applyCommit(docId, commit, opts) {
        const effectiveDocId = utils_1.typeDocID(docId);
        return document_1.default.applyCommit(this._apiUrl, effectiveDocId, commit, this.context, opts);
    }
    async applyRecord(docId, record, opts) {
        return this.applyCommit(docId, record, opts);
    }
    addDoctypeHandler(doctypeHandler) {
        this._doctypeConstructors[doctypeHandler.name] = doctypeHandler.doctype;
    }
    findDoctypeConstructor(doctype) {
        const constructor = this._doctypeConstructors[doctype];
        if (constructor) {
            return constructor;
        }
        else {
            throw new Error(`Failed to find doctype constructor for doctype ${doctype}`);
        }
    }
    async setDIDProvider(provider) {
        this.context.provider = provider;
        this.context.did = new dids_1.DID({ provider, resolver: this.context.resolver });
        if (!this.context.did.authenticated) {
            await this.context.did.authenticate();
        }
    }
    async getSupportedChains() {
        if (this._supportedChains) {
            return this._supportedChains;
        }
        const { supportedChains } = await utils_1.fetchJson(this._apiUrl + '/node/chains');
        this._supportedChains = supportedChains;
        return supportedChains;
    }
    async close() {
        this._docCache.applyToAll((d) => d.close());
        this._docCache.clear();
    }
}
exports.default = CeramicClient;
//# sourceMappingURL=ceramic-http-client.js.map